/**
 * 로컬(스탠드얼론) 모드 인증 — qbaseClient 의 인증 부분과 시그니처가 같다.
 * 백엔드 /api/local/auth/* (세션 쿠키). authSlice 는 이 파일을 쓰든 qbaseClient 를 쓰든 수정할 필요가 없다.
 */
import { BE1_API } from '../config/api';
import type { AuthSession } from './authTypes';
import i18n from '../i18n';
import { errorText } from './errorText';

export class QbaseError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'QbaseError';
    this.status = status;
  }
}

export const DEVICE_ID = 'openflow-web';

async function call(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${BE1_API}${path}`, { credentials: 'include', ...init });
  let body: any = null;
  try { body = await res.json(); } catch { /* 본문 없음 */ }
  if (!res.ok) throw new QbaseError(errorText(body, `${res.status} ${res.statusText}`), res.status);
  return body;
}

/** 미인증이면 null (401 은 오류가 아니라 '로그인 안 됨') */
export async function getSession(): Promise<AuthSession | null> {
  const res = await fetch(`${BE1_API}/api/local/auth/session`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new QbaseError(i18n.t('common:error.sessionQuery', { status: res.status }), res.status);
  return (await res.json()) as AuthSession;
}

export async function login(loginId: string, pwd: string): Promise<{ viewOnly: boolean }> {
  await call('/api/local/auth/login', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginId, pwd, deviceId: DEVICE_ID }),
  });
  return { viewOnly: false };  // 로컬 모드에는 조회 전용 상태가 없다
}

export async function logout(): Promise<void> {
  try { await call('/api/local/auth/logout', { method: 'POST' }); } catch { /* 실패해도 화면은 로그아웃 */ }
}

/** 로컬 모드에는 편집권 경쟁이 없다 — 잃어버린 갱신은 저장 시 expectedCurVerNo(409)로 막는다 */
export async function acquireEdit(): Promise<boolean> {
  try {
    const r = await call('/api/local/auth/acquire-edit', { method: 'POST' });
    return r?.acquired === true;
  } catch { return false; }
}

/** 로컬 모드의 시스템 선택은 서버가 고정값('LOCAL')으로 준다 — 추가 요청 없음 */
export async function ensureActiveSystem(session: AuthSession): Promise<string | null> {
  return session.activeSysId ?? session.homeSysId ?? null;
}
